import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Stack,
  IconButton, Tooltip, Divider, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Description as TemplateIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  Api as ApiIcon,
  Code as CodeIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Home as HomeIcon,
  Event as EventIcon,
  Person as PersonalIcon,
} from '@mui/icons-material';

// Flow template definitions
const FLOW_TEMPLATES = {
  // Basic Templates
  'simple-chat': {
    id: 'simple-chat',
    name: 'Simple Chat Bot',
    description: 'Basic conversational AI with user input and AI response',
    category: 'Basic',
    difficulty: 'Beginner',
    estimatedTime: '5 min',
    tags: ['chat', 'ai', 'conversation'],
    icon: <ChatIcon />,
    color: 'primary',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Chat Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Chat Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a helpful AI assistant. Respond to user questions in a friendly and informative way.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.7,
            maxTokens: 500,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 500, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'ai-agent_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'ai-agent_1',
        target: 'end_1',
        type: 'edge-success',
      },
    ],
    globalState: {},
  },

  'api-integration': {
    id: 'api-integration',
    name: 'API Integration',
    description: 'Fetch data from external API and process with AI',
    category: 'Integration',
    difficulty: 'Intermediate',
    estimatedTime: '10 min',
    tags: ['api', 'data', 'ai'],
    icon: <ApiIcon />,
    color: 'secondary',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'api-call_1',
        type: 'api-call',
        label: 'Fetch Data',
        position: { x: 300, y: 100 },
        data: {
          label: 'Fetch Data',
          type: 'api-call',
          config: {
            url: 'https://api.example.com/data',
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          },
        },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Process Data',
        position: { x: 500, y: 100 },
        data: {
          label: 'Process Data',
          type: 'ai-agent',
          config: {
            systemPrompt: 'Analyze the provided data and extract key insights. Present the information in a clear, structured format.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 1000,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'api-call_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'api-call_1',
        target: 'ai-agent_1',
        type: 'edge-success',
      },
      {
        id: 'edge_3',
        source: 'ai-agent_1',
        target: 'end_1',
        type: 'edge-success',
      },
    ],
    globalState: {},
  },

  'conditional-workflow': {
    id: 'conditional-workflow',
    name: 'Conditional Workflow',
    description: 'Branch logic based on conditions with different outcomes',
    category: 'Logic',
    difficulty: 'Intermediate',
    estimatedTime: '15 min',
    tags: ['conditional', 'logic', 'branching'],
    icon: <AssessmentIcon />,
    color: 'warning',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 200 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'conditional_1',
        type: 'conditional',
        label: 'Check Condition',
        position: { x: 300, y: 200 },
        data: {
          label: 'Check Condition',
          type: 'conditional',
          config: {
            condition: '{{user_input}} === "premium"',
            trueLabel: 'Premium User',
            falseLabel: 'Regular User',
          },
        },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Premium Response',
        position: { x: 500, y: 100 },
        data: {
          label: 'Premium Response',
          type: 'ai-agent',
          config: {
            systemPrompt: 'Provide premium-level assistance with detailed analysis and recommendations.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.5,
            maxTokens: 800,
          },
        },
      },
      {
        id: 'ai-agent_2',
        type: 'ai-agent',
        label: 'Standard Response',
        position: { x: 500, y: 300 },
        data: {
          label: 'Standard Response',
          type: 'ai-agent',
          config: {
            systemPrompt: 'Provide standard assistance with basic information and guidance.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.5,
            maxTokens: 400,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 200 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'conditional_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'conditional_1',
        target: 'ai-agent_1',
        type: 'edge-true',
      },
      {
        id: 'edge_3',
        source: 'conditional_1',
        target: 'ai-agent_2',
        type: 'edge-false',
      },
      {
        id: 'edge_4',
        source: 'ai-agent_1',
        target: 'end_1',
        type: 'edge-success',
      },
      {
        id: 'edge_5',
        source: 'ai-agent_2',
        target: 'end_1',
        type: 'edge-success',
      },
    ],
    globalState: {},
  },

  'real-estate-listing-agent': {
    id: 'real-estate-listing-agent',
    name: 'Real Estate Listing Agent',
    description: 'Find, evaluate, and synthesize property listings with budget triage, commute enrichment, and AI briefing',
    category: 'Real Estate',
    difficulty: 'Advanced',
    estimatedTime: '25 min',
    tags: ['real-estate', 'listings', 'ai', 'budget', 'pipeline'],
    icon: <HomeIcon />,
    color: 'success',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 200 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'market-discovery',
        type: 'code-executor',
        label: 'Market Discovery',
        position: { x: 300, y: 200 },
        data: {
          label: 'Market Discovery',
          type: 'code-executor',
          config: {
            language: 'python',
            code: `# Mock: Fetch property listings for target cities
targetCities = state.get('targetCities') or (state.get('input') or {}).get('cities') or ['Hayward', 'San Bruno']
maxBudget = state.get('maxBudget') or (state.get('input') or {}).get('budget') or 1150000

# Mock listings (replace with real API call later)
state['rawProperties'] = [
    {'address': '123 Main St, Hayward', 'price': 950000, 'beds': 3, 'baths': 2, 'sqft': 1800, 'city': 'Hayward'},
    {'address': '456 Oak Ave, Hayward', 'price': 1100000, 'beds': 4, 'baths': 2.5, 'sqft': 2200, 'city': 'Hayward'},
    {'address': '789 Pine Rd, San Bruno', 'price': 1050000, 'beds': 3, 'baths': 2, 'sqft': 1900, 'city': 'San Bruno'},
    {'address': '321 Elm St, San Bruno', 'price': 1250000, 'beds': 4, 'baths': 3, 'sqft': 2400, 'city': 'San Bruno'},
]
state['targetCities'] = targetCities
state['maxBudget'] = maxBudget
`,
          },
        },
      },
      {
        id: 'financial-triage',
        type: 'code-executor',
        label: 'Financial Triage',
        position: { x: 500, y: 200 },
        data: {
          label: 'Financial Triage',
          type: 'code-executor',
          config: {
            language: 'python',
            code: `# Filter by max budget, add estimated monthly carrying cost
raw = state.get('rawProperties', [])
max_budget = state.get('maxBudget', 1150000)
filtered = []
for p in raw:
    price = p.get('price', 0)
    if price <= max_budget:
        p_copy = dict(p)
        p_copy['estimatedMonthlyCost'] = round(price * 0.005)  # rough estimate
        filtered.append(p_copy)
state['filteredProperties'] = filtered
`,
          },
        },
      },
      {
        id: 'contextual-enrichment',
        type: 'code-executor',
        label: 'Contextual Enrichment',
        position: { x: 700, y: 200 },
        data: {
          label: 'Contextual Enrichment',
          type: 'code-executor',
          config: {
            language: 'python',
            code: `# Mock: Add morning commute time to office
import random
import json
filtered = state.get('filteredProperties', [])
enriched = []
for p in filtered:
    p_copy = dict(p)
    p_copy['commuteMinutes'] = 25 + random.randint(0, 30)  # 25-55 min mock
    enriched.append(p_copy)
state['enrichedProperties'] = enriched
state['hasActionableResults'] = len(enriched) > 0
# Set input for AI Synthesis
state['input'] = 'Summarize these properties in 3 bullet points (Markdown), highlighting trade-offs (budget vs commute):\\n' + json.dumps(enriched, indent=2)
`,
          },
        },
      },
      {
        id: 'ai-synthesis',
        type: 'ai-agent',
        label: 'AI Synthesis',
        position: { x: 900, y: 100 },
        data: {
          label: 'AI Synthesis',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a real estate analyst. You will receive a JSON list of property profiles (address, price, beds, baths, sqft, estimatedMonthlyCost, commuteMinutes). Generate a concise 3-bullet-point executive summary in Markdown. Highlight key trade-offs (e.g., "Property A is well under budget but has a 45-minute commute"). Be specific and actionable.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 800,
          },
        },
      },
      {
        id: 'webhook-egress',
        type: 'api-call',
        label: 'Webhook Egress',
        position: { x: 1100, y: 100 },
        data: {
          label: 'Webhook Egress',
          type: 'api-call',
          config: {
            url: 'https://webhook.site/placeholder',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{"briefing": "{{ai_response}}", "properties": {{enrichedProperties}}}',
            simulate: true,
          },
        },
      },
      {
        id: 'end_success',
        type: 'end',
        label: 'End',
        position: { x: 1300, y: 100 },
        data: { label: 'End', type: 'end' },
      },
      {
        id: 'end_silent',
        type: 'end',
        label: 'End (No Matches)',
        position: { x: 900, y: 300 },
        data: { label: 'End (No Matches)', type: 'end' },
      },
    ],
    edges: [
      { id: 'e1', source: 'start_1', target: 'market-discovery', type: 'edge-success' },
      { id: 'e2', source: 'market-discovery', target: 'financial-triage', type: 'edge-success' },
      { id: 'e3', source: 'financial-triage', target: 'contextual-enrichment', type: 'edge-success' },
      { id: 'e4', source: 'contextual-enrichment', target: 'ai-synthesis', type: 'edge-conditional', condition: 'hasActionableResults == true' },
      { id: 'e5', source: 'contextual-enrichment', target: 'end_silent', type: 'edge-conditional', condition: 'true' },
      { id: 'e6', source: 'ai-synthesis', target: 'webhook-egress', type: 'edge-success' },
      { id: 'e7', source: 'webhook-egress', target: 'end_success', type: 'edge-success' },
    ],
    globalState: {
      targetCities: ['Hayward', 'San Bruno'],
      maxBudget: 1150000,
    },
  },

  'email-automation': {
    id: 'email-automation',
    name: 'Email Automation',
    description: 'Automated email sending with AI-generated content',
    category: 'Automation',
    difficulty: 'Intermediate',
    estimatedTime: '12 min',
    tags: ['email', 'automation', 'ai'],
    icon: <EmailIcon />,
    color: 'info',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Generate Content',
        position: { x: 300, y: 100 },
        data: {
          label: 'Generate Content',
          type: 'ai-agent',
          config: {
            systemPrompt: 'Generate a professional email based on the provided context and recipient information.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.7,
            maxTokens: 600,
          },
        },
      },
      {
        id: 'email_1',
        type: 'email',
        label: 'Send Email',
        position: { x: 500, y: 100 },
        data: {
          label: 'Send Email',
          type: 'email',
          config: {
            to: '{{recipient_email}}',
            subject: '{{email_subject}}',
            body: '{{generated_content}}',
            from: 'noreply@company.com',
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'ai-agent_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'ai-agent_1',
        target: 'email_1',
        type: 'edge-success',
      },
      {
        id: 'edge_3',
        source: 'email_1',
        target: 'end_1',
        type: 'edge-success',
      },
    ],
    globalState: {},
  },

  'data-processing': {
    id: 'data-processing',
    name: 'Data Processing Pipeline',
    description: 'Process data with code execution and AI analysis',
    category: 'Data',
    difficulty: 'Advanced',
    estimatedTime: '20 min',
    tags: ['data', 'processing', 'code', 'ai'],
    icon: <CodeIcon />,
    color: 'success',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 200 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'code-executor_1',
        type: 'code-executor',
        label: 'Clean Data',
        position: { x: 300, y: 200 },
        data: {
          label: 'Clean Data',
          type: 'code-executor',
          config: {
            language: 'python',
            code: `import pandas as pd
import numpy as np

# Clean the data
df = pd.DataFrame({{input_data}})
df = df.dropna()
df = df.drop_duplicates()

# Return cleaned data
cleaned_data = df.to_dict('records')
print(f"Cleaned {len(cleaned_data)} records")`,
          },
        },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Analyze Data',
        position: { x: 500, y: 200 },
        data: {
          label: 'Analyze Data',
          type: 'ai-agent',
          config: {
            systemPrompt: 'Analyze the provided data and generate insights, trends, and recommendations.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 1200,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 200 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'code-executor_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'code-executor_1',
        target: 'ai-agent_1',
        type: 'edge-success',
      },
      {
        id: 'edge_3',
        source: 'ai-agent_1',
        target: 'end_1',
        type: 'edge-success',
      },
    ],
    globalState: {},
  },

  'loop-processing': {
    id: 'loop-processing',
    name: 'Loop Processing',
    description: 'Process multiple items in a loop with AI analysis',
    category: 'Logic',
    difficulty: 'Advanced',
    estimatedTime: '18 min',
    tags: ['loop', 'processing', 'ai', 'iteration'],
    icon: <TrendingIcon />,
    color: 'error',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 200 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'loop_1',
        type: 'loop',
        label: 'Process Items',
        position: { x: 300, y: 200 },
        data: {
          label: 'Process Items',
          type: 'loop',
          config: {
            array: '{{items_to_process}}',
            itemVariable: 'current_item',
            indexVariable: 'item_index',
          },
        },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Analyze Item',
        position: { x: 500, y: 200 },
        data: {
          label: 'Analyze Item',
          type: 'ai-agent',
          config: {
            systemPrompt: 'Analyze the current item and provide insights or recommendations.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.4,
            maxTokens: 300,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 700, y: 200 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'loop_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'loop_1',
        target: 'ai-agent_1',
        type: 'edge-success',
      },
      {
        id: 'edge_3',
        source: 'ai-agent_1',
        target: 'loop_1',
        type: 'edge-loop',
      },
      {
        id: 'edge_4',
        source: 'loop_1',
        target: 'end_1',
        type: 'edge-complete',
      },
    ],
    globalState: {},
  },

  // HR Onboarding Template
  'hr-onboarding': {
    id: 'hr-onboarding',
    name: 'HR Onboarding & Approvals',
    description: 'Complete employee onboarding workflow with approvals, IT setup, and notifications',
    category: 'Business',
    difficulty: 'Intermediate',
    estimatedTime: '15 min',
    tags: ['hr', 'onboarding', 'approvals', 'email', 'calendar', 'teams'],
    icon: <BusinessIcon />,
    color: 'secondary',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start Onboarding',
        position: { x: 100, y: 100 },
        data: { label: 'Start Onboarding', type: 'start' },
      },
      {
        id: 'form_1',
        type: 'form',
        label: 'Employee Details Form',
        position: { x: 300, y: 100 },
        data: {
          label: 'Employee Details Form',
          type: 'form',
          config: {
            fields: [
              {
                name: 'employeeName', label: 'Full Name', type: 'text', required: true,
              },
              {
                name: 'role', label: 'Job Role', type: 'text', required: true,
              },
              {
                name: 'startDate', label: 'Start Date', type: 'date', required: true,
              },
              {
                name: 'preferredLaptop', label: 'Preferred Laptop', type: 'select', options: ['Mac', 'Windows'], required: true,
              },
              {
                name: 'shirtSize', label: 'Shirt Size', type: 'select', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'], required: true,
              },
              {
                name: 'managerName', label: 'Manager Name', type: 'text', required: true,
              },
              {
                name: 'email', label: 'Email Address', type: 'email', required: true,
              },
            ],
          },
        },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Onboarding Assistant',
        position: { x: 500, y: 100 },
        data: {
          label: 'Onboarding Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are an HR onboarding assistant. Help new employees through the onboarding process with a friendly, welcoming tone. Ask for any missing information and guide them through the setup process.',
            model: 'gpt-4',
            temperature: 0.7,
          },
        },
      },
      {
        id: 'conditional_1',
        type: 'conditional',
        label: 'Check Required Info',
        position: { x: 700, y: 100 },
        data: {
          label: 'Check Required Info',
          type: 'conditional',
          config: {
            condition: '{{employeeName}} && {{role}} && {{startDate}} && {{preferredLaptop}} && {{shirtSize}} && {{managerName}} && {{email}}',
          },
        },
      },
      {
        id: 'api-call_1',
        type: 'api-call',
        label: 'Validate Compliance',
        position: { x: 500, y: 250 },
        data: {
          label: 'Validate Compliance',
          type: 'api-call',
          config: {
            url: 'https://api.company.com/compliance/validate',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer {{apiToken}}',
            },
            body: {
              employeeName: '{{employeeName}}',
              role: '{{role}}',
              startDate: '{{startDate}}',
            },
          },
        },
      },
      {
        id: 'email_1',
        type: 'email',
        label: 'Send IT Request',
        position: { x: 700, y: 250 },
        data: {
          label: 'Send IT Request',
          type: 'email',
          config: {
            to: 'it-setup@company.com',
            subject: 'New Employee IT Setup Request - {{employeeName}}',
            body: 'New employee {{employeeName}} ({{role}}) starting {{startDate}} needs:\n- {{preferredLaptop}} laptop\n- Company swag (size: {{shirtSize}})\n- Email: {{email}}\n- Manager: {{managerName}}',
          },
        },
      },
      {
        id: 'calendar_1',
        type: 'calendar',
        label: 'Schedule Orientation',
        position: { x: 900, y: 250 },
        data: {
          label: 'Schedule Orientation',
          type: 'calendar',
          config: {
            title: 'New Employee Orientation - {{employeeName}}',
            startDate: '{{startDate}}',
            endDate: '{{startDate}}',
            startTime: '09:00',
            endTime: '10:00',
            attendees: ['{{email}}', 'hr@company.com'],
            location: 'Conference Room A',
          },
        },
      },
      {
        id: 'email_2',
        type: 'email',
        label: 'Welcome Email',
        position: { x: 700, y: 400 },
        data: {
          label: 'Welcome Email',
          type: 'email',
          config: {
            to: '{{email}}',
            subject: 'Welcome to the Team, {{employeeName}}! 🎉',
            body: 'Hi {{employeeName}},\n\nWelcome to our team! We\'re excited to have you join us as {{role}} starting {{startDate}}.\n\nYour orientation session is scheduled for {{startDate}} at 9:00 AM in Conference Room A.\n\nHere\'s your onboarding checklist:\n- IT setup request submitted\n- Company swag ordered ({{shirtSize}})\n- Orientation scheduled\n- Manager: {{managerName}}\n\nWe look forward to seeing you on your first day!\n\nBest regards,\nHR Team',
          },
        },
      },
      {
        id: 'api-call_2',
        type: 'api-call',
        label: 'Post Welcome Message',
        position: { x: 900, y: 400 },
        data: {
          label: 'Post Welcome Message',
          type: 'api-call',
          config: {
            url: 'https://api.slack.com/api/chat.postMessage',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer {{slackToken}}',
            },
            body: {
              channel: '#general',
              text: '🎉 Welcome {{employeeName}} to our team! Starting as {{role}} on {{startDate}}. Say hello! 👋',
            },
          },
        },
      },
      {
        id: 'email_3',
        type: 'email',
        label: 'HR Confirmation',
        position: { x: 500, y: 550 },
        data: {
          label: 'HR Confirmation',
          type: 'email',
          config: {
            to: 'hr@company.com',
            subject: 'Onboarding Complete - {{employeeName}}',
            body: 'Onboarding process completed for {{employeeName}} ({{role}}):\n\n✅ Employee details collected\n✅ Compliance validated\n✅ IT setup requested\n✅ Orientation scheduled\n✅ Welcome email sent\n✅ Team announcement posted\n\nStart date: {{startDate}}\nManager: {{managerName}}\nEmail: {{email}}',
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'Onboarding Complete',
        position: { x: 700, y: 550 },
        data: { label: 'Onboarding Complete', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'form_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_2',
        source: 'form_1',
        target: 'ai-agent_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_3',
        source: 'ai-agent_1',
        target: 'conditional_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_4',
        source: 'conditional_1',
        target: 'api-call_1',
        type: 'edge-basic',
        condition: 'incomplete',
      },
      {
        id: 'edge_5',
        source: 'conditional_1',
        target: 'email_1',
        type: 'edge-basic',
        condition: 'complete',
      },
      {
        id: 'edge_6',
        source: 'api-call_1',
        target: 'email_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_7',
        source: 'email_1',
        target: 'calendar_1',
        type: 'edge-basic',
      },
      {
        id: 'edge_8',
        source: 'calendar_1',
        target: 'email_2',
        type: 'edge-basic',
      },
      {
        id: 'edge_9',
        source: 'email_2',
        target: 'api-call_2',
        type: 'edge-basic',
      },
      {
        id: 'edge_10',
        source: 'api-call_2',
        target: 'email_3',
        type: 'edge-basic',
      },
      {
        id: 'edge_11',
        source: 'email_3',
        target: 'end_1',
        type: 'edge-basic',
      },
    ],
    globalState: {
      employeeName: '',
      role: '',
      startDate: '',
      preferredLaptop: '',
      shirtSize: '',
      managerName: '',
      email: '',
      apiToken: '',
      slackToken: '',
    },
  },

  // Home Search Advisor Template
  'home-search-advisor': {
    id: 'home-search-advisor',
    name: 'Home Search Advisor',
    description: 'User describes housing requirements in text; flow searches properties via Zillow-style API and presents matching listings (demo mode uses simulated data)',
    category: 'Integration',
    difficulty: 'Intermediate',
    estimatedTime: '10 min',
    tags: ['real estate', 'property search', 'zillow', 'api', 'ai'],
    icon: <HomeIcon />,
    color: 'info',
    nodes: [
      {
        id: 'start_1',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: { label: 'Start', type: 'start' },
      },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Understand Requirements',
        position: { x: 300, y: 100 },
        data: {
          label: 'Understand Requirements',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a home search advisor. The user will describe their housing requirements in natural language (e.g., location, budget, bedrooms, bathrooms, preferences). Summarize their requirements clearly in 1-2 sentences and confirm you understand before searching. Be friendly and concise.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 300,
          },
        },
      },
      {
        id: 'api-call_1',
        type: 'api-call',
        label: 'Search Properties',
        position: { x: 500, y: 100 },
        data: {
          label: 'Search Properties',
          type: 'api-call',
          config: {
            url: 'https://api.zillow.com/v1/property-search',
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000,
            simulate: true,
            fallback_strategy: 'continue',
            fallback_data: {
              listings: [
                { address: '123 Oak Street, Austin TX', price: 425000, beds: 3, baths: 2, sqft: 1850, description: 'Charming single-family home with updated kitchen' },
                { address: '456 Maple Ave, Austin TX', price: 389000, beds: 3, baths: 2.5, sqft: 2100, description: 'Spacious home with large backyard' },
                { address: '789 Pine Rd, Austin TX', price: 475000, beds: 4, baths: 3, sqft: 2450, description: 'Modern open floor plan, master suite' },
              ],
            },
          },
        },
      },
      {
        id: 'ai-agent_2',
        type: 'ai-agent',
        label: 'Present Results',
        position: { x: 700, y: 100 },
        data: {
          label: 'Present Results',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a home search advisor. Present the property listings to the user in a clear, helpful format. Include address, price, beds/baths, sqft, and description for each. If the data came from api_response, api_responses, or similar, use that. Format as a friendly summary with bullet points or numbered list. If no listings, suggest refining the search criteria.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.4,
            maxTokens: 800,
          },
        },
      },
      {
        id: 'end_1',
        type: 'end',
        label: 'End',
        position: { x: 900, y: 100 },
        data: { label: 'End', type: 'end' },
      },
    ],
    edges: [
      {
        id: 'edge_1',
        source: 'start_1',
        target: 'ai-agent_1',
        type: 'edge-success',
      },
      {
        id: 'edge_2',
        source: 'ai-agent_1',
        target: 'api-call_1',
        type: 'edge-success',
      },
      {
        id: 'edge_3',
        source: 'api-call_1',
        target: 'ai-agent_2',
        type: 'edge-success',
      },
      {
        id: 'edge_4',
        source: 'ai-agent_2',
        target: 'end_1',
        type: 'edge-success',
      },
    ],
    globalState: {},
  },

  // InterviewFlow: Permit Discovery
  'permit-wizard': {
    id: 'permit-wizard',
    name: 'Permit Discovery',
    description: '"What permits do I need?" — Guided flow for permits, licenses, and applications. Ideal for citizen services.',
    category: 'Interview',
    difficulty: 'Beginner',
    estimatedTime: '8 min',
    tags: ['permits', 'citizen services', 'guided chat', 'intake'],
    icon: <BusinessIcon />,
    color: 'info',
    nodes: [
      { id: 'start_1', type: 'start', label: 'Start', position: { x: 100, y: 100 }, data: { label: 'Start', type: 'start' } },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Permit Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Permit Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a permit discovery assistant. Help citizens understand what permits they may need for their project. Ask about: 1) project type (construction, renovation, business, etc.), 2) location (city/county), 3) project scope/cost. Based on their answers, suggest relevant permits (building, zoning, business license, trade permits, etc.) and typical processing times. Be concise and helpful.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 600,
          },
        },
      },
      { id: 'end_1', type: 'end', label: 'End', position: { x: 500, y: 100 }, data: { label: 'End', type: 'end' } },
    ],
    edges: [
      { id: 'edge_1', source: 'start_1', target: 'ai-agent_1', type: 'edge-success' },
      { id: 'edge_2', source: 'ai-agent_1', target: 'end_1', type: 'edge-success' },
    ],
    globalState: {},
  },

  // InterviewFlow: Residential Permit Discovery (ADU, renovation, solar, deck)
  'residential-permit': {
    id: 'residential-permit',
    name: 'Residential Permit Discovery',
    description: 'ADU, renovation, solar, deck — Help homeowners discover what permits they need for residential projects.',
    category: 'Interview',
    difficulty: 'Beginner',
    estimatedTime: '8 min',
    tags: ['permits', 'residential', 'ADU', 'solar', 'deck', 'renovation'],
    icon: <HomeIcon />,
    color: 'info',
    nodes: [
      { id: 'start_1', type: 'start', label: 'Start', position: { x: 100, y: 100 }, data: { label: 'Start', type: 'start' } },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Residential Permit Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Residential Permit Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a residential permit discovery assistant. Help homeowners understand what permits they may need for: ADU (accessory dwelling unit), renovation/remodel, solar panels, deck/patio, fence, or similar residential projects. Ask about: 1) project type (ADU, renovation, solar, deck, other), 2) address or city/county, 3) project scope (sqft, cost estimate). Suggest relevant permits (building, electrical, solar, zoning, etc.) and typical timelines. Be concise and helpful.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 600,
          },
        },
      },
      { id: 'end_1', type: 'end', label: 'End', position: { x: 500, y: 100 }, data: { label: 'End', type: 'end' } },
    ],
    edges: [
      { id: 'edge_1', source: 'start_1', target: 'ai-agent_1', type: 'edge-success' },
      { id: 'edge_2', source: 'ai-agent_1', target: 'end_1', type: 'edge-success' },
    ],
    globalState: {},
  },

  // InterviewFlow: Special Events Permit Discovery
  'special-events-permit': {
    id: 'special-events-permit',
    name: 'Special Events Permit Discovery',
    description: 'Block parties, farmers markets, street closures — Guide applicants through events permitting.',
    category: 'Interview',
    difficulty: 'Beginner',
    estimatedTime: '8 min',
    tags: ['permits', 'events', 'block party', 'street closure', 'farmers market'],
    icon: <EventIcon />,
    color: 'info',
    nodes: [
      { id: 'start_1', type: 'start', label: 'Start', position: { x: 100, y: 100 }, data: { label: 'Start', type: 'start' } },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Events Permit Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Events Permit Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a special events permit assistant. Help applicants understand what permits they need for: block parties, farmers markets, street closures, outdoor festivals, parades, film shoots, or similar events. Ask about: 1) event type, 2) location and date, 3) expected attendance, 4) any street/venue closures. Suggest relevant permits (event permit, street closure, health dept, fire safety, etc.) and typical application timelines. Be concise and helpful.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.3,
            maxTokens: 600,
          },
        },
      },
      { id: 'end_1', type: 'end', label: 'End', position: { x: 500, y: 100 }, data: { label: 'End', type: 'end' } },
    ],
    edges: [
      { id: 'edge_1', source: 'start_1', target: 'ai-agent_1', type: 'edge-success' },
      { id: 'edge_2', source: 'ai-agent_1', target: 'end_1', type: 'edge-success' },
    ],
    globalState: {},
  },

  // InterviewFlow: Personal Assistant — simple version (fallback)
  'personal-assistant': {
    id: 'personal-assistant',
    name: 'Personal Assistant (Simple)',
    description: 'Help with emails, meetings, tasks. Draft emails, prep for meetings, triage tasks, get a day-at-a-glance.',
    category: 'Interview',
    difficulty: 'Beginner',
    estimatedTime: '10 min',
    tags: ['email', 'meetings', 'tasks', 'productivity', 'PA'],
    icon: <PersonalIcon />,
    color: 'primary',
    nodes: [
      { id: 'start_1', type: 'start', label: 'Start', position: { x: 100, y: 100 }, data: { label: 'Start', type: 'start' } },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Personal Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Personal Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a helpful personal assistant. Help the user with: 1) Drafting emails—ask for recipient, purpose, tone, key points; 2) Meeting prep—ask for meeting title, attendees, agenda, and provide talking points and questions to ask; 3) Task triage—accept a list of tasks and prioritize with suggested time blocks; 4) Day-at-a-glance—summarize meetings and top tasks. Be concise and actionable.',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.4,
            maxTokens: 800,
          },
        },
      },
      { id: 'end_1', type: 'end', label: 'End', position: { x: 500, y: 100 }, data: { label: 'End', type: 'end' } },
    ],
    edges: [
      { id: 'edge_1', source: 'start_1', target: 'ai-agent_1', type: 'edge-success' },
      { id: 'edge_2', source: 'ai-agent_1', target: 'end_1', type: 'edge-success' },
    ],
    globalState: {},
  },

  // Comprehensive Org Personal Assistant — role-based, intent-driven, email/meeting summaries
  // Connector nodes can replace API calls for real Gmail/Outlook/Google Calendar integration
  'org-personal-assistant': {
    id: 'org-personal-assistant',
    name: 'Org Personal Assistant',
    description: 'Role-based personal assistant: summarize emails and meetings by query, day brief, draft emails, task prioritization, schedule finder. Different output style per role (executive, manager, team).',
    category: 'Interview',
    difficulty: 'Intermediate',
    estimatedTime: '20 min',
    tags: ['email', 'meetings', 'calendar', 'tasks', 'productivity', 'role-based', 'summarize', 'executive'],
    icon: <PersonalIcon />,
    color: 'secondary',
    nodes: [
      { id: 'start_1', type: 'start', label: 'Start', position: { x: 100, y: 200 }, data: { label: 'Start', type: 'start' } },
      {
        id: 'fetch-data',
        type: 'api-call',
        label: 'Fetch Emails & Meetings',
        position: { x: 300, y: 200 },
        data: {
          label: 'Fetch Emails & Meetings',
          type: 'api-call',
          config: {
            url: 'https://api.example.com/assistant/data',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
            timeout: 15000,
            simulate: true,
            fallback_strategy: 'continue',
            fallback_data: {
              emails: [
                { from: 'boss@company.com', subject: 'Q4 Review - Action Required', date: '2024-01-15', snippet: 'Please review the attached deck before our meeting...', priority: 'high' },
                { from: 'client@external.com', subject: 'Contract renewal discussion', date: '2024-01-14', snippet: 'Looking forward to our call tomorrow...', priority: 'medium' },
                { from: 'team@company.com', subject: 'Standup notes', date: '2024-01-15', snippet: 'Completed: API integration. Blocked: None.', priority: 'low' },
              ],
              meetings: [
                { title: 'Q4 Review', time: '10:00 AM', duration: 60, attendees: ['John', 'Sarah'], location: 'Conf Room A', agenda: 'Review quarterly results' },
                { title: 'Client Call - Acme', time: '2:00 PM', duration: 30, attendees: ['Client'], location: 'Zoom', agenda: 'Contract discussion' },
                { title: 'Team Standup', time: '9:00 AM', duration: 15, attendees: ['Team'], location: 'Slack', agenda: 'Daily sync' },
              ],
              freeSlots: [
                { start: '10:30 AM', end: '11:00 AM' },
                { start: '3:00 PM', end: '4:00 PM' },
              ],
            },
          },
        },
      },
      {
        id: 'assistant',
        type: 'ai-agent',
        label: 'Personal Assistant',
        position: { x: 520, y: 200 },
        data: {
          label: 'Personal Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: `You are a comprehensive organizational personal assistant. You support different roles with tailored responses:

**Roles & Output Style:**
- **Executive**: Concise, 30-second read. Top 3–5 items only. No fluff.
- **Manager**: Group by theme/project. Highlight action items and delegation.
- **Team**: Full detail, context, next steps, and estimates.

**Capabilities (respond based on user intent):**

1. **Email Summary** – "What are my important emails?" / "Summarize emails from X"
   - Use emails from api_response/fallback_data. Filter by query (e.g., "urgent", "from John", "this week").
   - Format: sender, subject, key point, action needed.

2. **Meeting Summary** – "What meetings do I have?" / "Summarize my meetings"
   - Use meetings from api_response/fallback_data. Filter by query.
   - Format: time, title, attendees, agenda, prep notes if relevant.

3. **Day-at-a-Glance** – "What's my day look like?" / "Give me a brief"
   - Combine meetings + top emails + suggested focus. Time-ordered.

4. **Draft Email** – "Draft an email to X about Y"
   - Generate professional email. Match tone to role.

5. **Task Prioritization** – "Prioritize these tasks" / "What should I focus on?"
   - Order by urgency, impact, dependencies. Suggest time blocks.

6. **Schedule Finder** – "When am I free?" / "Find a slot for a meeting"
   - Use freeSlots from data. Suggest best options.

7. **Meeting Prep** – "Prep me for the Q4 Review meeting"
   - Use meeting + related emails. Provide talking points, questions to ask, potential objections.

8. **General** – Answer helpfully, infer role from context.

Always adapt your response length and detail to the user's role. Use the data provided—if api_response or fallback_data contains emails/meetings, reference them.`,
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.4,
            maxTokens: 1200,
          },
        },
      },
      { id: 'end_1', type: 'end', label: 'End', position: { x: 740, y: 200 }, data: { label: 'End', type: 'end' } },
    ],
    edges: [
      { id: 'e1', source: 'start_1', target: 'fetch-data', type: 'edge-success' },
      { id: 'e2', source: 'fetch-data', target: 'assistant', type: 'edge-success' },
      { id: 'e3', source: 'assistant', target: 'end_1', type: 'edge-success' },
    ],
    globalState: {},
  },

  // InterviewFlow: Return Assistant
  'return-assistant': {
    id: 'return-assistant',
    name: 'Return Assistant',
    description: '"I want to return something" — Guided return policy and process. Order #, reason, condition → next steps.',
    category: 'Interview',
    difficulty: 'Beginner',
    estimatedTime: '8 min',
    tags: ['returns', 'customer service', 'guided chat', 'policy'],
    icon: <ChatIcon />,
    color: 'info',
    nodes: [
      { id: 'start_1', type: 'start', label: 'Start', position: { x: 100, y: 100 }, data: { label: 'Start', type: 'start' } },
      {
        id: 'ai-agent_1',
        type: 'ai-agent',
        label: 'Return Assistant',
        position: { x: 300, y: 100 },
        data: {
          label: 'Return Assistant',
          type: 'ai-agent',
          config: {
            systemPrompt: 'You are a return assistant. Help customers with returns. Ask for: 1) order number, 2) reason for return, 3) item condition (unopened, used, damaged). Check if within 30-day window. Explain policy: within window + good condition = full refund; within window + used = partial/exchange; outside window = contact support. Give clear next steps: print label, pack, ship, refund timeline (5-7 days).',
            model: 'llama4',
            provider: 'llama-api',
            temperature: 0.2,
            maxTokens: 500,
          },
        },
      },
      { id: 'end_1', type: 'end', label: 'End', position: { x: 500, y: 100 }, data: { label: 'End', type: 'end' } },
    ],
    edges: [
      { id: 'edge_1', source: 'start_1', target: 'ai-agent_1', type: 'edge-success' },
      { id: 'edge_2', source: 'ai-agent_1', target: 'end_1', type: 'edge-success' },
    ],
    globalState: {},
  },
};

// Template categories
const TEMPLATE_CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: <TemplateIcon /> },
  { id: 'Basic', name: 'Basic', icon: <StarIcon /> },
  { id: 'Interview', name: 'Interview / Guided', icon: <ChatIcon /> },
  { id: 'Integration', name: 'Integration', icon: <ApiIcon /> },
  { id: 'Logic', name: 'Logic', icon: <AssessmentIcon /> },
  { id: 'Automation', name: 'Automation', icon: <ScheduleIcon /> },
  { id: 'Data', name: 'Data', icon: <CodeIcon /> },
];

// Difficulty levels
const DIFFICULTY_LEVELS = {
  Beginner: { color: 'success', icon: <CheckIcon /> },
  Intermediate: { color: 'warning', icon: <InfoIcon /> },
  Advanced: { color: 'error', icon: <TrendingIcon /> },
};

// Main Flow Templates Component
function FlowTemplates({ open, onClose, onApplyTemplate }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const filteredTemplates = Object.values(FLOW_TEMPLATES).filter((template) => selectedCategory === 'all' || template.category === selectedCategory);

  const handleApplyTemplate = useCallback((template) => {
    onApplyTemplate(template);
    onClose();
  }, [onApplyTemplate, onClose]);

  const handlePreviewTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setPreviewOpen(true);
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon color="primary" />
            <Typography variant="h5" component="h2">
              Flow Templates
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Categories Sidebar */}
            <Box sx={{
              width: 200,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Categories
                </Typography>
                <List dense>
                  {TEMPLATE_CATEGORIES.map((category) => (
                    <ListItem
                      key={category.id}
                      button
                      selected={selectedCategory === category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.lighter',
                          '&:hover': {
                            bgcolor: 'primary.light',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {category.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={category.name}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>

            {/* Templates Grid */}
            <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {selectedCategory === 'all' ? 'All Templates' : `${selectedCategory} Templates`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredTemplates.length}
                  {' '}
                  template(s) available
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {filteredTemplates.map((template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardContent sx={{ flex: 1, pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Box sx={{
                            color: `${template.color}.main`,
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          >
                            {template.icon}
                          </Box>
                          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                            {template.name}
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {template.description}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip
                            label={template.difficulty}
                            size="small"
                            color={DIFFICULTY_LEVELS[template.difficulty].color}
                            icon={DIFFICULTY_LEVELS[template.difficulty].icon}
                          />
                          <Chip
                            label={template.estimatedTime}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Nodes:
                            {' '}
                            {template.nodes.length}
                            {' '}
                            | Edges:
                            {' '}
                            {template.edges.length}
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {template.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20, mb: 0.5 }}
                            />
                          ))}
                        </Stack>
                      </CardContent>

                      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handlePreviewTemplate(template)}
                          sx={{ mr: 1 }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PlayIcon />}
                          onClick={() => handleApplyTemplate(template)}
                        >
                          Use Template
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {filteredTemplates.length === 0 && (
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
                >
                  <TemplateIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No templates found
                  </Typography>
                  <Typography variant="body2">
                    Try selecting a different category
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedTemplate?.icon}
            <Typography variant="h6">
              {selectedTemplate?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => setPreviewOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedTemplate.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Difficulty
                  </Typography>
                  <Chip
                    label={selectedTemplate.difficulty}
                    color={DIFFICULTY_LEVELS[selectedTemplate.difficulty].color}
                    icon={DIFFICULTY_LEVELS[selectedTemplate.difficulty].icon}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Estimated Time
                  </Typography>
                  <Chip
                    label={selectedTemplate.estimatedTime}
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Flow Structure
              </Typography>
              <Box sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Nodes:</strong>
                  {' '}
                  {selectedTemplate.nodes.length}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Connections:</strong>
                  {' '}
                  {selectedTemplate.edges.length}
                </Typography>
                <Typography variant="body2">
                  <strong>Tags:</strong>
                  {' '}
                  {selectedTemplate.tags.join(', ')}
                </Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2, mt: 3 }}>
                Node Types Used
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {[...new Set(selectedTemplate.nodes.map((n) => n.type))].map((type) => (
                  <Chip
                    key={type}
                    label={type.replace('-', ' ')}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={() => {
              handleApplyTemplate(selectedTemplate);
              setPreviewOpen(false);
            }}
          >
            Use This Template
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default FlowTemplates;
